import { IPromise, IQService } from 'angular';
import {
    FONT_POSTURE_OPTIONS,
    FONT_WEIGHT_OPTIONS,
    FONT_WIDTH_OPTIONS,
    FontListParams,
    FontListParamsStateParamsResolver,
    FontListParamsToStateParams,
    FontPosture,
    FontStyleService,
    FontWeight,
    FontWidth,
    IFontFamilyModel,
    IFontStyleModel,
    IPeopleModel,
    Option,
} from '../../../tn.common';

import { PeopleService } from '../../../tn.fonts/tn.fonts.service/people';
import template from './family-page-sidebar.html';
import {
    StateParams,
    StateService,
    Transition,
    TransitionService,
} from 'angular-ui-router';
import { ISeries, SeriesService } from '../../../tn.fonts/SeriesService';

export class FamilySidebarController {
    // bindings
    family?: IFontFamilyModel;
    filters: FontListParams = {};

    // locals
    categories = {};
    designers: IPeopleModel[] = [];
    foundry = [];
    onStateParamsChangedDebounceTimeout?: number;
    postures: Option<FontPosture>[] = [];
    series: ISeries[] = [];
    styles: IFontStyleModel[] = [];
    weights: Option<FontWeight>[] = [];
    widths: Option<FontWidth>[] = [];

    static $inject = [
        '$q',
        '$state',
        '$stateParams',
        '$transitions',
        'tn.people.service',
        'FontStylePublicService',
        'seriesService',
    ];
    constructor(
        protected $q: IQService,
        protected $state: StateService,
        protected $stateParams: StateParams,
        protected $transitions: TransitionService,
        protected peopleService: PeopleService,
        protected styleService: FontStyleService,
        protected seriesService: SeriesService
    ) {
        const onTransitionSuccess = (transition: Transition) => {
            const params = transition.params();
            this.onStateParamsChanged(params);
        };

        // the filters parameters are all dynamic to avoid rendering the entire
        // ui. since resolvers are not ran on dynamic transitions, catch the onSuccess
        // and update the filters.
        // we only catch transitions to the same state, since initial state loads should be
        $transitions.onSuccess(
            { to: 'home.foundry.fonts.styles' },
            onTransitionSuccess
        );
    }

    $onInit(): void {
        this.onStateParamsChanged(this.$stateParams);
    }

    onStateParamsChanged(params: Partial<StateParams>): void {
        this.filters = FontListParamsStateParamsResolver(params);
        // debounce the data load so we're not totally hammering the
        // server as people toggle things on and off...
        if (this.onStateParamsChangedDebounceTimeout) {
            window.clearTimeout(this.onStateParamsChangedDebounceTimeout);
        }
        this.onStateParamsChangedDebounceTimeout = window.setTimeout(() => {
            this.onStateParamsChangedDebounceTimeout = undefined;
            this.getData();
        }, 300);
    }

    clearFilters(): void {
        this.$state.go(
            '.',
            {
                foundryEESubdomain: this.family?.canonical_foundry.ee_subdomain,
                familyName: this.family?.slug,
            },
            { inherit: false }
        );
    }

    getData(): IPromise<void> {
        const family = this.family;
        if (!family) {
            return this.$q.resolve();
        }

        const stylePromise = this.styleService
            .list({
                family: [family.id],
            })
            .then(({ results: styles }) => {
                this.styles = styles;

                this.postures = FONT_POSTURE_OPTIONS.filter((option) =>
                    styles.some((style) => style.posture === option.value)
                );
                this.weights = FONT_WEIGHT_OPTIONS.filter((option) =>
                    styles.some((style) => style.weight === option.value)
                );
                this.widths = FONT_WIDTH_OPTIONS.filter((option) =>
                    styles.some((style) => style.width === option.value)
                );
                // console.log('FamilySidebarController.getData', {
                //     family,
                //     styles,
                //     postures: this.postures,
                //     weights: this.weights,
                //     widths: this.widths,
                // });
            });

        this.seriesService.getSeriesByIds(family.series).then((series) => {
            this.series = series;
        });

        // get designers in parallel
        this.peopleService.getPeople(family.designer).then((designers) => {
            this.designers = designers;
        });

        return stylePromise;
    }

    modifyState(params: Partial<FontListParams>): void {
        const stateParams = FontListParamsToStateParams({
            ...this.filters,
            ...params,
        });
        // console.log('FamilySidebarController.modifyState', stateParams);
        this.$state.go('.', stateParams);
    }

    clearFilter(filter: string): void {
        delete this.filters[filter];
        const stateParams = FontListParamsToStateParams(this.filters);
        this.$state.go('.', stateParams);
    }

    hasFilter(key: keyof FontListParams, value: unknown): boolean {
        const filter = this.filters[key];
        if (filter == undefined) return false;
        if (value == undefined) {
            return Array.isArray(filter) ? filter.length > 0 : filter !== '';
        }
        return Array.isArray(filter)
            ? (filter as unknown[]).includes(value)
            : filter === value;
    }
}

export const FamilySidebarComponent = {
    bindings: {
        family: '<',
        filters: '<',
    },
    controller: FamilySidebarController,
    controllerAs: 'vm',
    template,
};
